import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  FormGroup,
  Form,
  Input,
  Row,
  Col,
  Table,
  Dropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from "reactstrap";
import { IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CoreSpinner from '../components/_core/CoreSpinner';
import { get, post, put, deleteItem, postReturning } from '../actions/api';

// Blank form state. Field names match the API request body (snake_case).
const emptyForm = {
  id: null,
  name: "",
  template_id: "",
  server: "",
  database: "",
  auth_client_id: "",
  client_secret: "",
  query: "",
  enabled: true
};

function ConfigureConnection() {
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(false);      // true when the form is open
  const [hasSecret, setHasSecret] = useState(false);   // editing an existing secret
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [templateDropdown, setTemplateDropdown] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [conns, temps] = await Promise.all([
        get("connections"),
        get("templates")
      ]);
      setConnections(conns || []);
      setTemplates(temps || []);
    } catch {
      setError("Failed to load connections.");
    }
    setLoading(false);
  }

  const templateName = (id) => templates.find(t => t.id === id)?.title || `Template #${id}`;

  const handleField = (evt) => {
    setError(null);
    const { name, value } = evt.target;
    setForm({ ...form, [name]: value });
  };

  function startCreate() {
    setForm(emptyForm);
    setHasSecret(false);
    setTestResult(null);
    setError(null);
    setEditing(true);
  }

  function startEdit(conn) {
    setForm({
      id: conn.id,
      name: conn.name || "",
      template_id: conn.template_id || "",
      server: conn.server || "",
      database: conn.database || "",
      auth_client_id: conn.auth_client_id || "",
      client_secret: "",           // never returned; blank = keep existing
      query: conn.query || "",
      enabled: conn.enabled
    });
    setHasSecret(conn.has_secret);
    setTestResult(null);
    setError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setForm(emptyForm);
    setTestResult(null);
    setError(null);
  }

  function validate() {
    if (!form.name.trim()) return "Name is required.";
    if (!form.template_id) return "A template must be selected.";
    if (!form.server.trim()) return "Server is required.";
    if (!form.auth_client_id.trim()) return "Client ID is required.";
    if (!form.query.trim()) return "Query is required.";
    return null;
  }

  function buildBody() {
    const body = {
      template_id: Number(form.template_id),
      name: form.name,
      server: form.server,
      database: form.database,
      auth_client_id: form.auth_client_id,
      query: form.query,
      enabled: form.enabled
    };
    // Only send the secret when the user typed one (blank keeps the stored value).
    if (form.client_secret) body.client_secret = form.client_secret;
    return body;
  }

  async function onSave(e) {
    e.preventDefault();
    const problem = validate();
    if (problem) return setError(problem);
    setSaving(true);
    setError(null);
    try {
      if (form.id) {
        await put(`connections/${form.id}`, buildBody());
      } else {
        await post("connections", buildBody());
      }
      await loadAll();
      cancelEdit();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save the connection.");
    }
    setSaving(false);
  }

  async function onDelete(conn) {
    if (!window.confirm(`Delete connection "${conn.name}"?`)) return;
    setLoading(true);
    try {
      await deleteItem(`connections/${conn.id}`);
      await loadAll();
    } catch {
      setError("Failed to delete the connection.");
      setLoading(false);
    }
  }

  async function onTest() {
    const problem = validate();
    if (problem) return setError(problem);
    setTesting(true);
    setTestResult(null);
    setError(null);
    try {
      const body = {
        server: form.server,
        database: form.database,
        auth_client_id: form.auth_client_id,
        query: form.query
      };
      if (form.client_secret) body.client_secret = form.client_secret;
      // Reuse the stored secret when editing without re-entering it.
      if (form.id) body.connection_id = form.id;
      const result = await postReturning("connections/test", body);
      setTestResult(result);
    } catch (err) {
      setTestResult({ success: false, error: err.response?.data?.error || "Test request failed." });
    }
    setTesting(false);
  }

  function prettySample(json) {
    if (!json) return "";
    try {
      return JSON.stringify(JSON.parse(json), null, 2);
    } catch {
      return json;
    }
  }

  return (
    <div className="content">
      {loading && <CoreSpinner loading={loading} />}
      {!loading && (
        <Row>
          <Col md="12">
            {/* ---- Connection list ---- */}
            <Card>
              <CardHeader>
                <Row className="align-items-center">
                  <Col>
                    <h5 className="title">Configure Connection</h5>
                    <p className="card-category">
                      Database connections that map a query to a template. Used by the
                      Template Builder's <strong>Load from Database</strong> option.{" "}
                      <a
                        href="#help"
                        onClick={(e) => { e.preventDefault(); setHelpOpen(true); }}
                        style={{ color: "#4083ed", fontWeight: 600 }}
                      >
                        How do I create a connection?
                      </a>
                    </p>
                  </Col>
                  <Col className="text-right">
                    <Button className="btn-fill" color="primary" onClick={startCreate}>
                      + New Connection
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {connections.length === 0 ? (
                  <p>No connections configured yet.</p>
                ) : (
                  <Table responsive>
                    <thead className="text-primary">
                      <tr>
                        <th>Name</th>
                        <th>Template</th>
                        <th>Server</th>
                        <th>Database</th>
                        <th>Enabled</th>
                        <th className="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {connections.map(conn => (
                        <tr key={conn.id}>
                          <td>{conn.name}</td>
                          <td>{templateName(conn.template_id)}</td>
                          <td>{conn.server}</td>
                          <td>{conn.database}</td>
                          <td>{conn.enabled ? "Yes" : "No"}</td>
                          <td className="text-right">
                            <IconButton title="Edit" onClick={() => startEdit(conn)}>
                              <EditIcon style={{ fontSize: 20, color: "#4083ed" }} />
                            </IconButton>
                            <IconButton title="Delete" onClick={() => onDelete(conn)}>
                              <DeleteIcon style={{ fontSize: 20, color: "#d9534f" }} />
                            </IconButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>

            {/* ---- Create / edit form ---- */}
            {editing && (
              <Card>
                <Form onSubmit={onSave}>
                  <CardHeader>
                    <h5 className="title">{form.id ? "Edit Connection" : "New Connection"}</h5>
                    {error && <p style={{ color: "red" }}>{error}</p>}
                  </CardHeader>
                  <CardBody>
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <label>Name</label>
                          <Input
                            name="name"
                            maxLength={100}
                            placeholder="Connection name"
                            value={form.name}
                            onChange={handleField}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label>Template</label>
                          <br />
                          <Dropdown isOpen={templateDropdown} toggle={() => setTemplateDropdown(p => !p)}>
                            <DropdownToggle caret style={{ padding: "10px 18px" }}>
                              {form.template_id ? templateName(Number(form.template_id)) : "Select template"}
                            </DropdownToggle>
                            <DropdownMenu style={{ maxHeight: 220, overflowY: 'auto' }}>
                              {templates.map(t => (
                                <DropdownItem
                                  key={t.id}
                                  onClick={() => { setForm({ ...form, template_id: t.id }); setError(null); }}
                                >
                                  {t.title}
                                </DropdownItem>
                              ))}
                            </DropdownMenu>
                          </Dropdown>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <label>Server</label>
                          <Input
                            name="server"
                            maxLength={200}
                            placeholder="myserver.database.windows.net"
                            value={form.server}
                            onChange={handleField}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label>Database</label>
                          <Input
                            name="database"
                            maxLength={128}
                            placeholder="Database name"
                            value={form.database}
                            onChange={handleField}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <label>Client ID (service principal)</label>
                          <Input
                            name="auth_client_id"
                            maxLength={100}
                            placeholder="Application (client) ID"
                            value={form.auth_client_id}
                            onChange={handleField}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label>
                            Client Secret
                            {hasSecret && <span style={{ color: "#888" }}> (stored — leave blank to keep)</span>}
                          </label>
                          <Input
                            name="client_secret"
                            type="password"
                            autoComplete="new-password"
                            placeholder={hasSecret ? "••••••••" : "Client secret"}
                            value={form.client_secret}
                            onChange={handleField}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="12">
                        <FormGroup>
                          <label>Query (returns a single column/row of JSON)</label>
                          <Input
                            name="query"
                            type="textarea"
                            rows={4}
                            placeholder="SELECT TOP 1 jsonText FROM ccpClinical.PapLetter"
                            value={form.query}
                            onChange={handleField}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="12">
                        <FormGroup check>
                          <label style={{ cursor: "pointer" }}>
                            <Input
                              type="checkbox"
                              checked={form.enabled}
                              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                            />{" "}
                            Enabled (available in the Builder's Load from Database list)
                          </label>
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* ---- Test results ---- */}
                    {testResult && (
                      <div style={{ marginTop: 15 }}>
                        {testResult.success ? (
                          <>
                            <p style={{ color: "green", fontWeight: "bold" }}>
                              Success — {testResult.record_count} record(s) returned.
                            </p>
                            {testResult.sample_json && (
                              <pre style={{
                                maxHeight: 300, overflow: "auto", background: "#1e1e2f",
                                color: "#e0e0e0", padding: 12, borderRadius: 6, fontSize: 12
                              }}>
                                {prettySample(testResult.sample_json)}
                              </pre>
                            )}
                          </>
                        ) : (
                          <p style={{ color: "red", fontWeight: "bold" }}>
                            Failed — {testResult.error}
                          </p>
                        )}
                      </div>
                    )}
                  </CardBody>
                  <CardFooter>
                    <Button type="submit" className="btn-fill" color="primary" disabled={saving}>
                      {saving ? "Saving…" : "Save"}
                    </Button>
                    <Button
                      type="button"
                      style={{ marginLeft: 10 }}
                      className="btn-fill"
                      color="info"
                      onClick={onTest}
                      disabled={testing}
                    >
                      {testing ? "Testing…" : "Test Connection"}
                    </Button>
                    <Button
                      type="button"
                      style={{ marginLeft: 10 }}
                      className="btn-fill"
                      color="secondary"
                      onClick={cancelEdit}
                    >
                      Cancel
                    </Button>
                  </CardFooter>
                </Form>
              </Card>
            )}
          </Col>
        </Row>
      )}

      {/* ---- Help: how to create a connection ---- */}
      <Modal isOpen={helpOpen} toggle={() => setHelpOpen(false)} size="lg" scrollable>
        <ModalHeader toggle={() => setHelpOpen(false)}>How to create a connection</ModalHeader>
        <ModalBody>
          <p>
            A connection lets a template pull its data straight from your customer database.
            The <strong>Client ID</strong> and <strong>Client Secret</strong> are an Entra
            <em> service principal</em> (app registration) that SDP uses to sign in to that
            database. Create them once in Azure, then enter them below.
          </p>

          <h6 className="mt-3">1. Get an app registration (Client ID)</h6>
          <p>
            In the Entra tenant that owns the database, open{" "}
            <strong>App registrations</strong> and create a new registration (or reuse an
            existing one). Copy its <strong>Application (client) ID</strong> — that is the
            <strong> Client ID</strong> field on the form.
          </p>

          <h6 className="mt-3">2. Create the client secret</h6>
          <p>
            On that app registration go to <strong>Certificates &amp; secrets → Client
            secrets → New client secret</strong>. Set a description and an expiry, click
            Add, then <strong>copy the <em>Value</em> immediately</strong> — Azure shows it
            only once. Paste it into the <strong>Client Secret</strong> field.
          </p>
          <p className="text-muted" style={{ fontSize: 13 }}>
            Secrets expire. When one lapses, the connection starts failing with an auth error — create
            a new secret and paste it into the connection (nothing else changes).
          </p>

          <h6 className="mt-3">3. Grant the service principal read access</h6>
          <p>
            The secret only authenticates — the principal still needs rights inside the
            target database. Connected as an Entra admin, run (using the app registration's
            name):
          </p>
          <pre style={{
            background: "#1e1e2f", color: "#e0e0e0", padding: 12,
            borderRadius: 6, fontSize: 12, overflowX: "auto"
          }}>{`CREATE USER [your-app-registration-name] FROM EXTERNAL PROVIDER;
ALTER ROLE db_datareader ADD MEMBER [your-app-registration-name];`}</pre>
          <p className="text-muted" style={{ fontSize: 13 }}>
            Use a read-only principal (<code>db_datareader</code>) — the Load-from-Database
            path only runs SELECT statements, so it never needs write access.
          </p>

          <h6 className="mt-3">4. Enter it and test</h6>
          <p>
            Fill in Server, Database, Client ID, Client Secret, and the Query (which must
            return a single row whose first column is the JSON array), then click{" "}
            <strong>Test Connection</strong>. A green record count confirms the secret and
            grant work. After saving, the secret is encrypted at rest — when you edit a
            connection later, leave Client Secret blank to keep the stored value.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => setHelpOpen(false)}>Got it</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default ConfigureConnection;
